/* <fitpod-trend-graph>: Web Component around Fitpod.graph.mount() (assets/fitpod-graph.js, unchanged from the design system).
   Draws in once when 35% is on screen (1400 ms + 320 ms settle), follows pointer and Left/Right arrow keys,
   reduced motion shows the finished graph. Options come from the JSON in <script type="application/json" data-graph>.
   Optional "sets" ({ w, d30, d90 }) with [data-period] buttons in the same section switch the period: the graph
   draws again (graphs.md §7 "New data: update plays the draw again"; never morphs). Example data only.
   Auto-switch (user decision, review log R81): with sets, start on "start" (30 days) and move on to the next period
   every 5.5 s (30 days > 90 days > week), only while the section is on screen, the tab is visible and the pointer
   or keyboard focus is not on the graph. A click on a period stops it for good. Reduced motion: no auto-switch. */
const AUTO_ORDER = ["d30", "d90", "w"];
const AUTO_DELAY = 5500;

class FitpodTrendGraph extends HTMLElement {
  connectedCallback() {
    if (this.api) return;
    const plot = this.querySelector("[data-plot]");
    const conf = this.querySelector("script[data-graph]");
    if (!plot || !conf || !window.Fitpod || !window.Fitpod.graph) return;
    let opts;
    try { opts = JSON.parse(conf.textContent); } catch (e) { return; }
    const live = this.querySelector("[data-graph-live]");
    const template = this.dataset.selectedTemplate || "";
    const sets = opts.sets; delete opts.sets;
    const weekTicks = opts.weekTicks; delete opts.weekTicks;
    const start = sets && sets[opts.start] ? opts.start : null; delete opts.start;
    if (start) {
      opts.data = sets[start];
      if (start === "w") opts.ticks = weekTicks;
    }
    const announce = (d) => {
      if (live && d) live.textContent = template.replace("__value__", Number(d.value).toFixed(2)).replace("__unit__", opts.unit || "kg").replace("__date__", d.time || d.label);
    };
    opts.onSelect = (i) => announce(this.api && this.current ? this.current[i] : opts.data[i]);
    this.current = opts.data;
    this.api = window.Fitpod.graph.mount(plot, opts);
    plot.setAttribute("role", "group");

    const scope = this.closest("section");
    if (!sets || !scope) return;
    const buttons = Array.from(scope.querySelectorAll("[data-period]"));
    let period = start || "w";

    const show = (p) => {
      period = p;
      buttons.forEach((x) => x.setAttribute("aria-pressed", String(x.dataset.period === p)));
      this.current = sets[p];
      this.api.update({ data: this.current, ticks: p === "w" ? weekTicks : undefined, selected: null }, true);
    };

    // Auto-switch: runs only while every condition below holds; any change re-checks and restarts the 5.5 s wait.
    let reduce = false;
    try { reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}
    const state = { stopped: reduce, onScreen: false, hover: false, focus: false };
    let timer = null;
    const schedule = () => {
      clearTimeout(timer); timer = null;
      if (state.stopped || !state.onScreen || state.hover || state.focus || document.hidden) return;
      timer = setTimeout(() => {
        const next = AUTO_ORDER[(AUTO_ORDER.indexOf(period) + 1) % AUTO_ORDER.length];
        show(next); // silent: auto changes are not announced to screen readers
        schedule();
      }, AUTO_DELAY);
    };
    this.stopAuto = () => { state.stopped = true; schedule(); };

    buttons.forEach((b) => b.addEventListener("click", () => {
      this.stopAuto();
      show(b.dataset.period);
      announce(this.current[this.current.length - 1]);
    }));
    if (state.stopped) return;

    const figure = this.closest("figure") || this;
    figure.addEventListener("pointerenter", (e) => { if (e.pointerType === "mouse") { state.hover = true; schedule(); } });
    figure.addEventListener("pointerleave", (e) => { if (e.pointerType === "mouse") { state.hover = false; schedule(); } });
    figure.addEventListener("focusin", () => { state.focus = true; schedule(); });
    figure.addEventListener("focusout", (e) => { if (!figure.contains(e.relatedTarget)) { state.focus = false; schedule(); } });
    this.onVisibility = () => schedule();
    document.addEventListener("visibilitychange", this.onVisibility);
    this.observer = new IntersectionObserver((entries) => {
      state.onScreen = entries[0].isIntersecting;
      schedule();
    }, { threshold: 0.35 });
    this.observer.observe(this);
    this.clearAuto = () => clearTimeout(timer);
  }
  disconnectedCallback() {
    if (this.clearAuto) this.clearAuto();
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
    if (this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.api) { this.api.destroy(); this.api = null; }
  }
}
if (!customElements.get("fitpod-trend-graph")) customElements.define("fitpod-trend-graph", FitpodTrendGraph);
